/**
 * Cart API Service - Backend Custom API Client
 *
 * Service para interactuar con los endpoints de carrito del Backend Custom API.
 * Este servicio NO usa Supabase directo, sino que consume los endpoints REST
 * del backend Node.js que están documentados en openapi.yaml.
 *
 * **Endpoints soportados:**
 * - GET /api/cart - Obtener carrito del usuario
 * - POST /api/cart - Agregar item al carrito
 * - PUT /api/cart/{artwork_id} - Actualizar cantidad de item
 * - DELETE /api/cart/{artwork_id} - Eliminar item del carrito
 * - DELETE /api/cart - Vaciar carrito completo
 * - POST /api/cart/sync - Sincronizar carrito local con servidor
 *
 * **Migración desde Supabase:**
 * Este servicio reemplaza cartService.js que usaba Supabase directo.
 * El backend Custom API maneja:
 * - Validaciones de negocio (límites de cantidad, artwork disponible)
 * - Cálculo de totales y descuentos
 * - Merge de carritos después de login
 *
 * @module services/cartApiService
 *
 * @example
 * import { cartApiService } from '@/services/cartApiService';
 *
 * // Agregar item al carrito
 * const { data, error } = await cartApiService.addItemToCart('artwork-uuid', 2);
 */

import { apiClient } from '@/utils/apiClient';

/**
 * Cart API Service class
 */
class CartApiService {
  /**
   * Obtener carrito del usuario autenticado
   *
   * **Flujo Backend:**
   * 1. Valida autenticación del usuario
   * 2. Obtiene items del carrito con información actualizada de artworks
   * 3. Verifica disponibilidad de cada item
   * 4. Calcula totales (subtotal, descuentos, total)
   * 5. Retorna carrito completo con metadata
   *
   * **Endpoint:** GET /api/cart
   *
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   * @returns {Object} data - Carrito del usuario
   * @returns {Array} data.items - Items del carrito
   * @returns {number} data.subtotal - Subtotal antes de descuentos
   * @returns {number} data.discount_amount - Monto de descuento aplicado
   * @returns {number} data.total - Total final del carrito
   * @returns {Object} data.metadata - Metadata del carrito
   * @returns {number} data.metadata.unique_items - Cantidad de artworks únicos
   * @returns {number} data.metadata.total_items - Cantidad total de items (con cantidades)
   *
   * @example
   * const { success, data, error } = await cartApiService.getCart();
   *
   * if (success) {
   *   console.log('Items en carrito:', data.items.length);
   *   console.log('Total:', data.total);
   * } else {
   *   console.error('Error:', error);
   * }
   */
  async getCart() {
    return await apiClient.get('/api/cart');
  }

  /**
   * Agregar item al carrito
   *
   * **Flujo Backend:**
   * 1. Valida que el artwork existe y está publicado
   * 2. Verifica que el usuario no es el artista del artwork
   * 3. Valida límites de cantidad (1-10, o 1 para artworks únicos)
   * 4. Si el item ya existe, incrementa la cantidad
   * 5. Si es nuevo, lo agrega al carrito
   * 6. Recalcula totales del carrito
   * 7. Retorna item agregado + carrito actualizado
   *
   * **Endpoint:** POST /api/cart
   *
   * @param {string} artwork_id - UUID del artwork a agregar
   * @param {number} [quantity=1] - Cantidad a agregar (1-10)
   *
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   * @returns {Object} data - Resultado de agregar item
   * @returns {Object} data.item - Item agregado/actualizado
   * @returns {string} data.item.id - UUID del item en el carrito
   * @returns {string} data.item.artwork_id - UUID del artwork
   * @returns {number} data.item.quantity - Cantidad en el carrito
   * @returns {Object} data.cart - Carrito completo actualizado
   *
   * @example
   * const { success, data, error } = await cartApiService.addItemToCart('550e8400-...', 2);
   *
   * if (success) {
   *   console.log('Item agregado:', data.item.artwork_id);
   *   console.log('Total carrito:', data.cart.total);
   * } else {
   *   console.error('Error:', error);
   * }
   */
  async addItemToCart(artwork_id, quantity = 1) {
    return await apiClient.post('/api/cart', { artwork_id, quantity });
  }

  /**
   * Actualizar cantidad de item en el carrito
   *
   * **Flujo Backend:**
   * 1. Valida que el item existe en el carrito del usuario
   * 2. Valida que la cantidad está en rango válido (1-10)
   * 3. Verifica límites del artwork (único vs replicable)
   * 4. Si la cantidad es mayor a la disponible, ajusta automáticamente
   * 5. Actualiza la cantidad del item
   * 6. Recalcula totales del carrito
   * 7. Retorna item actualizado + carrito
   *
   * **Endpoint:** PUT /api/cart/{artwork_id}
   *
   * @param {string} artwork_id - UUID del artwork en el carrito
   * @param {number} quantity - Nueva cantidad (1-10)
   *
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   * @returns {Object} data - Resultado de actualización
   * @returns {Object} data.item - Item actualizado
   * @returns {number} data.item.quantity - Nueva cantidad
   * @returns {Object} data.cart - Carrito completo actualizado
   *
   * @example
   * const { success, data, error } = await cartApiService.updateCartItem('550e8400-...', 3);
   *
   * if (success) {
   *   console.log('Cantidad actualizada:', data.item.quantity);
   * } else {
   *   console.error('Error:', error);
   * }
   */
  async updateCartItem(artwork_id, quantity) {
    return await apiClient.put(`/api/cart/${artwork_id}`, { quantity });
  }

  /**
   * Eliminar item del carrito
   *
   * **Flujo Backend:**
   * 1. Valida que el item existe en el carrito del usuario
   * 2. Elimina el item del carrito
   * 3. Recalcula totales del carrito
   * 4. Retorna carrito actualizado
   *
   * **Endpoint:** DELETE /api/cart/{artwork_id}
   *
   * @param {string} artwork_id - UUID del artwork a eliminar
   *
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   * @returns {Object} data - Carrito actualizado
   * @returns {Object} data.cart - Carrito completo sin el item eliminado
   *
   * @example
   * const { success, data, error } = await cartApiService.removeCartItem('550e8400-...');
   *
   * if (success) {
   *   console.log('Item eliminado, nuevo total:', data.cart.total);
   * } else {
   *   console.error('Error:', error);
   * }
   */
  async removeCartItem(artwork_id) {
    return await apiClient.delete(`/api/cart/${artwork_id}`);
  }

  /**
   * Vaciar carrito completo
   *
   * **Flujo Backend:**
   * 1. Elimina todos los items del carrito del usuario
   * 2. Resetea totales a 0
   * 3. Retorna carrito vacío
   *
   * **Advertencia:** Esta acción no puede deshacerse
   *
   * **Endpoint:** DELETE /api/cart
   *
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   * @returns {Object} data - Carrito vacío
   * @returns {Object} data.cart - Carrito con items: [], total: 0
   *
   * @example
   * const { success, data, error } = await cartApiService.clearCart();
   *
   * if (success) {
   *   console.log('Carrito vaciado exitosamente');
   *   console.log('Items restantes:', data.cart.items.length); // 0
   * } else {
   *   console.error('Error:', error);
   * }
   */
  async clearCart() {
    return await apiClient.delete('/api/cart');
  }

  /**
   * Sincronizar carrito local con servidor
   *
   * **Flujo Backend:**
   * 1. Compara items locales con items del servidor
   * 2. Agrega items que solo existen localmente
   * 3. Actualiza cantidades donde sea apropiado
   * 4. Reporta conflictos y items no disponibles
   * 5. Retorna carrito sincronizado + resultados del merge
   *
   * **Casos de uso:**
   * - Sincronizar después de login
   * - Recuperar carrito después de desconexión
   * - Merge de carritos entre dispositivos
   *
   * **Endpoint:** POST /api/cart/sync
   *
   * @param {Array} local_items - Items del carrito local a sincronizar
   * @param {string} local_items[].artwork_id - UUID del artwork
   * @param {number} local_items[].quantity - Cantidad (1-10)
   * @param {string} [local_items[].added_at] - Fecha agregado (ISO 8601)
   *
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   * @returns {Object} data - Resultado de sincronización
   * @returns {Object} data.sync_results - Estadísticas del merge
   * @returns {number} data.sync_results.added - Items agregados desde local
   * @returns {number} data.sync_results.updated - Items con cantidad actualizada
   * @returns {Array} data.sync_results.conflicts - Items con conflictos
   * @returns {Array} data.sync_results.unavailable - Items no disponibles
   * @returns {Object} data.cart - Carrito completo sincronizado
   *
   * @example
   * const local_items = [
   *   { artwork_id: '550e8400-...', quantity: 2, added_at: '2025-01-15T10:30:00Z' },
   *   { artwork_id: '550e8400-...', quantity: 1, added_at: '2025-01-15T11:15:00Z' }
   * ];
   *
   * const { success, data, error } = await cartApiService.syncCart(local_items);
   *
   * if (success) {
   *   console.log('Sincronización completada');
   *   console.log('Items agregados:', data.sync_results.added);
   *   console.log('Items actualizados:', data.sync_results.updated);
   *   console.log('Conflictos:', data.sync_results.conflicts);
   *   console.log('Total items en carrito:', data.cart.items.length);
   * } else {
   *   console.error('Error en sincronización:', error);
   * }
   */
  async syncCart(local_items = []) {
    return await apiClient.post('/api/cart/sync', { local_items });
  }
}

export const cartApiService = new CartApiService();
